import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Chatbot from "./Chatbot.tsx";
import ParentDashboard from "./ParentDashboard.tsx";
import RankListComponent from "./RankList.tsx";
import DroctoIQDashboard from "./ParentDashboard.tsx";
import SocialMedia from "./SocialMedia.tsx";
import Health from "./Health.tsx";
import Library from "./Library.tsx";
import Lottie from "lottie-react";
import octopusAnimation from "../assets/Octopus.json";
import loadingAnimation from "../assets/Loading.json";
import mydashboard from "../assets/mydashboard.jpg";

interface PlanAndSupportCardProps {
  plan: string;
}

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
}

interface ConnectWithSchoolProps {
  schoolDetails: SchoolDetails | null;
  onConnect: (schoolCode: string) => Promise<void>;
}

interface SchoolDetails {
  schoolCode: string;
  schoolName: string;
  city: string;
  pincode: string;
  schoolImage?: string;
}

interface UserData {
  childName: string;
  childImage: string;
  ageYears: number;
  displayAgeDays: number;
  totalAgeDays: number;
  userId: string;
  plan: string;
  schoolDetails: SchoolDetails | null;
}

interface UserApiResponse {
  fullName: string;
  childImage?: string;
  dob?: string;
  whatsapp: string;
  plan: string;
  schoolDetails?: SchoolDetails;
  language?: string;
}

const PlanAndSupportCard: React.FC<PlanAndSupportCardProps> = ({ plan }) => (
  <div className="mt-4 agent-card animate-fade-in">
    <div className="flex justify-between items-center">
      <div>
        <h3 className="text-lg font-bold mb-2">Active Plan</h3>
        <p className="text-sm text-cyber-green">Plan: {plan}</p>
      </div>
      <div className="flex items-center gap-2">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="text-cyber-green"
        >
          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
          <polyline points="7 10 12 15 17 10" />
          <line x1="12" y1="15" x2="12" y2="3" />
        </svg>
        <a href="mailto:support@example.com" className="text-sm text-cyber-green">
          Need Support?
        </a>
      </div>
    </div>
  </div>
);

const ConnectWithSchool: React.FC<ConnectWithSchoolProps> = ({ schoolDetails, onConnect }) => {
  const [schoolCode, setSchoolCode] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    if (!/^\d{6}$/.test(schoolCode)) {
      setError("School code must be a 6-digit number");
      setLoading(false);
      return;
    }

    try {
      await onConnect(schoolCode);
      setSchoolCode("");
    } catch (err: any) {
      setError(err.message || "Failed to connect school. Please check the code and try again.");
    } finally {
      setLoading(false);
    }
  };

  if (schoolDetails) {
    return (
      <div className="mt-4 agent-card animate-fade-in connect-school-card">
        <div className="flex items-center gap-3 mb-3">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="text-cyan-400"
          >
            <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
            <polyline points="9 22 9 12 15 12 15 22" />
          </svg>
          <h3 className="text-lg font-bold text-cyan-400">Connected School</h3>
        </div>
        <div className="flex items-center gap-4">
          {schoolDetails.schoolImage && (
            <img
              src={schoolDetails.schoolImage}
              alt={schoolDetails.schoolName}
              className="w-16 h-16 rounded-lg object-cover"
            />
          )}
          <div>
            <p className="text-sm font-semibold text-gray-200">{schoolDetails.schoolName}</p>
            <p className="text-sm text-gray-400">
              {schoolDetails.city}, {schoolDetails.pincode}
            </p>
            <p className="text-sm text-gray-400">Code: {schoolDetails.schoolCode}</p>
          </div>
        </div>
        {error && <p className="text-sm text-cosmic-pink mt-2">{error}</p>}
      </div>
    );
  }

  return (
    <div className="mt-4 agent-card animate-fade-in connect-school-card">
      <div className="flex items-center gap-3 mb-3">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="text-cyan-400"
        >
          <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
          <polyline points="9 22 9 12 15 12 15 22" />
        </svg>
        <h3 className="text-lg font-bold text-cyan-400">Connect with a School</h3>
      </div>
      <p className="text-sm text-gray-200 flex items-center gap-2 mb-3">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="text-cyan-400"
        >
          <path d="M12 2v6l3 3" />
          <path d="M12 22a7 7 0 0 0 7-7c0-2-1-3.9-3-5.5" />
          <path d="M12 22a7 7 0 0 1-7-7c0-2 1-3.9 3-5.5" />
        </svg>
        Enter your school's 6-digit code to unlock exciting features
      </p>
      <form onSubmit={handleSubmit} className="flex flex-col gap-2">
        <input
          type="text"
          value={schoolCode}
          onChange={(e) => setSchoolCode(e.target.value)}
          placeholder="Enter 6-digit school code"
          className="p-2 rounded-lg bg-gray-800 text-white border border-gray-600 focus:border-cyan-400 focus:outline-none"
          maxLength={6}
        />
        {error && <p className="text-sm text-cosmic-pink">{error}</p>}
        <button
          type="submit"
          className="language-button connect-button"
          disabled={loading}
        >
          {loading ? (
            "Connecting..."
          ) : (
            <>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="inline mr-2"
              >
                <path d="M18 6 6 18" />
                <path d="m6 6 12 12" />
              </svg>
              Connect Now
            </>
          )}
        </button>
      </form>
    </div>
  );
};

const Modal: React.FC<ModalProps> = ({ isOpen, onClose, children }) => {
  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <button className="modal-close" onClick={onClose}>
          ×
        </button>
        {children}
      </div>
    </div>
  );
};

const ThinkyPlayProfile: React.FC = () => {
  const [userData, setUserData] = useState<UserData>({
    childName: "",
    childImage: "",
    ageYears: 0,
    displayAgeDays: 0,
    totalAgeDays: 0,
    userId: "",
    plan: "Silver",
    schoolDetails: null,
  });
  const [points, setPoints] = useState<number>(0);
  const [dailyPercentage, setDailyPercentage] = useState<number>(0);
  const [completedChats, setCompletedChats] = useState<number>(0);
  const [stateRank, setStateRank] = useState<number>(0);
  const [completedTasks, setCompletedTasks] = useState<string[]>([]);
  const [tasks, setTasks] = useState<string[]>([]);
  const [isParentExpanded, setIsParentExpanded] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>("");
  const [chatCompleted, setChatCompleted] = useState<boolean>(false);
  const [language, setLanguage] = useState<string>("English");
  const [currentView, setCurrentView] = useState<"profile" | "leaderboard" | "iq" | "socialmedia" | "health" | "content" | "chat">("profile");
  const [theme, setTheme] = useState<string>(localStorage.getItem("theme") || "black");
  const [isSettingsExpanded, setIsSettingsExpanded] = useState<boolean>(false);

  const navigate = useNavigate();

  const calculateAge = (dob: string): { ageYears: number; displayAgeDays: number; totalAgeDays: number } => {
    const birthDate = new Date(dob);
    const today = new Date();

    if (isNaN(birthDate.getTime()) || birthDate > today) {
      return { ageYears: 0, displayAgeDays: 0, totalAgeDays: 0 };
    }

    const diffMs = today.getTime() - birthDate.getTime();
    const totalAgeDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    let ageYears = today.getFullYear() - birthDate.getFullYear();
    const birthThisYear = new Date(today.getFullYear(), birthDate.getMonth(), birthDate.getDate());

    if (today < birthThisYear) {
      ageYears--;
    }

    const lastBirthday = new Date(
      today.getFullYear() - (today < birthThisYear ? 1 : 0),
      birthDate.getMonth(),
      birthDate.getDate()
    );
    let displayAgeDays = Math.floor((today.getTime() - lastBirthday.getTime()) / (1000 * 60 * 60 * 24));

    if (displayAgeDays < 0) {
      const previousBirthday = new Date(
        today.getFullYear() - 1,
        birthDate.getMonth(),
        birthDate.getDate()
      );
      displayAgeDays = Math.floor((today.getTime() - previousBirthday.getTime()) / (1000 * 60 * 60 * 24));
      ageYears--;
    }

    return {
      ageYears: Math.max(0, ageYears),
      displayAgeDays: Math.max(0, displayAgeDays),
      totalAgeDays: Math.max(0, totalAgeDays),
    };
  };

  useEffect(() => {
    const fetchAllData = async () => {
      try {
        const token = localStorage.getItem("token");
        if (!token) throw new Error("No authentication token found");

        const userResponse = await fetch(`${process.env.REACT_APP_API_URL}/api/auth/user`, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });

        if (!userResponse.ok) {
          const contentType = userResponse.headers.get("content-type");
          let errorData;
          if (contentType && contentType.includes("application/json")) {
            errorData = await userResponse.json();
          } else {
            const errorText = await userResponse.text();
            throw new Error(`Unexpected response: ${errorText}`);
          }
          if (userResponse.status === 401 && errorData.message === "Token expired, please log in again") {
            localStorage.removeItem("token");
            navigate("/login");
            return;
          }
          throw new Error(errorData.message || "Failed to fetch user data");
        }

        const userData: UserApiResponse = await userResponse.json();
        console.log("API Response:", userData);
        console.log("DOB:", userData.dob);

        if (!userData.dob || isNaN(new Date(userData.dob).getTime())) {
          setError("Invalid or missing date of birth.");
          setLoading(false);
          return;
        }

        const { ageYears, displayAgeDays, totalAgeDays } = calculateAge(userData.dob);
        if (totalAgeDays <= 0) {
          setError("Invalid age calculated.");
          setLoading(false);
          return;
        }

        const newUserData: UserData = {
          childName: userData.fullName,
          childImage: userData.childImage || "",
          ageYears,
          displayAgeDays,
          totalAgeDays,
          userId: userData.whatsapp,
          plan: userData.plan,
          schoolDetails: userData.schoolDetails || null,
        };
        setUserData(newUserData);
        setLanguage(userData.language || "English");

        if (!newUserData.userId) {
          throw new Error("User ID not available");
        }

        const [progressResponse, rankResponse] = await Promise.all([
          fetch(`${process.env.REACT_APP_API_URL}/api/auth/user-progress/${newUserData.userId}`, {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          }),
          fetch(`${process.env.REACT_APP_API_URL}/api/rankings/state/${newUserData.userId}`, {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          }),
        ]);

        if (progressResponse.ok) {
          const progressData = await progressResponse.json();
          setPoints((prev) => (progressData.points > 0 ? progressData.points : prev));
          setDailyPercentage((prev) => (progressData.dailyPercentage > 0 ? progressData.dailyPercentage : prev));
          setCompletedChats((prev) => (progressData.completedChats >= 0 ? progressData.completedChats : prev));
        } else {
          const contentType = progressResponse.headers.get("content-type");
          let errorData;
          if (contentType && contentType.includes("application/json")) {
            errorData = await progressResponse.json();
            if (progressResponse.status === 401 && errorData.message === "Token expired, please log in again") {
              localStorage.removeItem("token");
              navigate("/login");
              return;
            }
            if (progressResponse.status === 404) {
              console.log("No progress data found, preserving current values");
            } else {
              throw new Error(errorData.message || "Failed to fetch progress data");
            }
          } else {
            const errorText = await progressResponse.text();
            throw new Error(`Unexpected progress response: ${errorText}`);
          }
        }

        if (rankResponse.ok) {
          const rankData = await rankResponse.json();
          setStateRank(rankData.myRank ? rankData.myRank.rank : 0);
        } else {
          const contentType = rankResponse.headers.get("content-type");
          let errorData;
          if (contentType && contentType.includes("application/json")) {
            errorData = await rankResponse.json();
            if (rankResponse.status === 401 && errorData.message === "Token expired, please log in again") {
              localStorage.removeItem("token");
              navigate("/login");
              return;
            }
            if (rankResponse.status === 404) {
              setStateRank(0);
            } else {
              throw new Error(errorData.message || "Failed to fetch state rank");
            }
          } else {
            const errorText = await rankResponse.text();
            throw new Error(`Unexpected rank response: ${errorText}`);
          }
        }
      } catch (err: any) {
        setError(err.message || "Error fetching data");
      } finally {
        setLoading(false);
      }
    };

    fetchAllData();
  }, [navigate]);

  const connectSchool = async (schoolCode: string) => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/school/connect-school`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ schoolCode, userId: userData.userId }),
      });

      if (!response.ok) {
        const contentType = response.headers.get("content-type");
        let errorData;
        if (contentType && contentType.includes("application/json")) {
          errorData = await response.json();
        } else {
          const errorText = await response.text();
          throw new Error(`Unexpected response: ${errorText}`);
        }
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setUserData((prev) => ({
        ...prev,
        schoolDetails: data.schoolDetails,
      }));
    } catch (err: any) {
      throw new Error(err.message || "Failed to connect school");
    }
  };

  useEffect(() => {
    if (isParentExpanded) {
      const dashboardElement = document.querySelector(".modal-content");
      if (dashboardElement) {
        dashboardElement.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }
  }, [isParentExpanded]);

  const handleLanguageChange = async (selectedLanguage: string) => {
    try {
      const token = localStorage.getItem("token");
      if (!token) throw new Error("No authentication token found");

      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/auth/user/language`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ language: selectedLanguage }),
      });

      if (!response.ok) {
        const contentType = response.headers.get("content-type");
        let errorData;
        if (contentType && contentType.includes("application/json")) {
          errorData = await response.json();
        } else {
          const errorText = await response.text();
          throw new Error(`Unexpected response: ${errorText}`);
        }
        throw new Error(errorData.message || "Failed to update language");
      }

      setLanguage(selectedLanguage);
    } catch (err: any) {
      setError(err.message || "Error updating language");
    }
  };

  const handleThemeChange = (selectedTheme: string) => {
    setTheme(selectedTheme);
    localStorage.setItem("theme", selectedTheme);
  };

  const ProfileCard: React.FC = () => (
    <div className="mt-4 agent-card">
      <img
        src={mydashboard}
        alt="Dashboard Banner"
        className="banner-image"
      />
      <div className="flex items-center gap-4 mt-4">
        {userData.childImage ? (
          <img
            src={userData.childImage}
            alt="Profile"
            className="w-12 h-12 rounded-full object-cover"
          />
        ) : (
          <div className="agent-icon">{userData.childName[0]}</div>
        )}
        <div>
          <h2 className="text-xl font-bold">{userData.childName}</h2>
          <p className="text-sm opacity-80">
            {userData.ageYears} year{userData.ageYears !== 1 ? "s" : ""} and{" "}
            {userData.displayAgeDays} day{userData.displayAgeDays !== 1 ? "s" : ""}
          </p>
        </div>
      </div>
      <div className="mt-4 grid grid-cols-2 gap-4">
        <div className="text-center">
          <p className="text-2xl font-bold">{points}</p>
          <p className="text-xs opacity-80">IQ Points</p>
        </div>
        <div className="text-center">
          <p className="text-2xl font-bold">#{stateRank}</p>
          <p className="text-xs opacity-80">State Rank</p>
        </div>
        <div className="text-center">
          <p className="text-2xl font-bold">{dailyPercentage}%</p>
          <p className="text-xs opacity-80">Daily Smartness</p>
        </div>
        <div className="text-center">
          <p className="text-2xl font-bold">{completedChats}</p>
          <p className="text-xs opacity-80">Days Completed</p>
        </div>
      </div>
      <div className="mt-4">
        <div className="progress-bar-container w-full bg-white/20 rounded-full h-4 relative overflow-hidden">
          <div
            className="progress-bar bg-gradient-to-r from-electric-cyan to-cyber-green rounded-full h-full transition-all duration-500"
            style={{ width: `${dailyPercentage}%` }}
          />
        </div>
      </div>
    </div>
  );

  const LanguageSelection: React.FC = () => (
    <div className="mt-4">
      <h3 className="text-lg font-bold mb-2">Language</h3>
      <div className="flex gap-2 justify-center mb-4">
        {["English", "Hindi", "Hinglish"].map((lang) => (
          <button
            key={lang}
            onClick={() => handleLanguageChange(lang)}
            className={`language-button ${language === lang ? "selected" : ""}`}
          >
            {lang}
          </button>
        ))}
      </div>
      <h3 className="text-lg font-bold mb-2">Theme</h3>
      <div className="flex gap-2 justify-center">
        {[
          { name: "black", color: "#000000" },
          { name: "white", color: "#f5f5f5" },
          { name: "pink", color: "#fff0f6" },
        ].map((themeOption) => (
          <button
            key={themeOption.name}
            onClick={() => handleThemeChange(themeOption.name)}
            className={`theme-circle ${theme === themeOption.name ? "active" : ""}`}
            style={{
              backgroundColor: themeOption.color,
              width: theme === themeOption.name ? "28px" : "20px",
              height: theme === themeOption.name ? "28px" : "20px",
            }}
            title={`Switch to ${themeOption.name} theme`}
          />
        ))}
      </div>
    </div>
  );

  const SettingsCard: React.FC = () => (
    <div className="mt-4 agent-card animate-fade-in">
      <button
        className="flex justify-between items-center w-full text-left"
        onClick={() => setIsSettingsExpanded(!isSettingsExpanded)}
      >
        <h3 className="text-lg font-bold">Settings</h3>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className={`transform transition-transform ${isSettingsExpanded ? "rotate-180" : ""}`}
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>
      {isSettingsExpanded && <LanguageSelection />}
    </div>
  );

  const handleChatCompletion = () => {
    setChatCompleted(false);
    const fetchProgressAndRank = async () => {
      try {
        const token = localStorage.getItem("token");
        if (!token) throw new Error("No authentication token found");

        const [progressResponse, rankResponse] = await Promise.all([
          fetch(`${process.env.REACT_APP_API_URL}/api/auth/user-progress/${userData.userId}`, {
            headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          }),
          fetch(`${process.env.REACT_APP_API_URL}/api/rankings/state/${userData.userId}`, {
            headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          }),
        ]);

        if (progressResponse.ok) {
          const progressData = await progressResponse.json();
          setPoints((prev) => (progressData.points > 0 ? progressData.points : prev));
          setDailyPercentage((prev) => (progressData.dailyPercentage > 0 ? progressData.dailyPercentage : prev));
          setCompletedChats((prev) => (progressData.completedChats >= 0 ? progressData.completedChats : prev));
        } else {
          const contentType = progressResponse.headers.get("content-type");
          let errorData;
          if (contentType && contentType.includes("application/json")) {
            errorData = await progressResponse.json();
            if (progressResponse.status === 401 && errorData.message === "Token expired, please log in again") {
              localStorage.removeItem("token");
              navigate("/login");
              return;
            }
            if (progressResponse.status === 404) {
              console.log("No progress data found, preserving current values");
            } else {
              throw new Error(errorData.message || "Failed to fetch progress data");
            }
          } else {
            const errorText = await progressResponse.text();
            throw new Error(`Unexpected progress response: ${errorText}`);
          }
        }

        if (rankResponse.ok) {
          const rankData = await rankResponse.json();
          setStateRank(rankData.myRank ? rankData.myRank.rank : 0);
        } else {
          const contentType = rankResponse.headers.get("content-type");
          let errorData;
          if (contentType && contentType.includes("application/json")) {
            errorData = await rankResponse.json();
            if (rankResponse.status === 401 && errorData.message === "Token expired, please log in again") {
              localStorage.removeItem("token");
              navigate("//login");
              return;
            }
            if (rankResponse.status === 404) {
              setStateRank(0);
            } else {
              throw new Error(errorData.message || "Failed to fetch state rank");
            }
          } else {
            const errorText = await rankResponse.text();
            throw new Error(`Unexpected rank response: ${errorText}`);
          }
        }
      } catch (err: any) {
        console.error("Error refetching progress/rank:", err.message || "Unknown error");
      }
    };
    fetchProgressAndRank();
  };

  const handleChatButtonClick = () => {
    setCurrentView("chat");
  };

  const handleIQButtonClick = () => {
    setCurrentView("iq");
  };

  const handleLeaderboardButtonClick = () => {
    setCurrentView("leaderboard");
  };

  const handleHealthButtonClick = () => {
    setCurrentView("health");
  };

  const handleContentButtonClick = () => {
    setCurrentView("content");
  };

  if (loading) {
    return (
      <div
        className="min-h-screen flex items-center justify-center bg-[var(--background-color)]"
        aria-label="Loading profile data"
      >
        <Lottie
          animationData={loadingAnimation}
          loop={true}
          style={{ width: "200px", height: "200px" }}
        />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-cosmic-pink text-center min-h-screen flex items-center justify-center">
        {error}
      </div>
    );
  }

  return (
    <div className={`relative min-h-screen theme-${theme}`}>
      <style jsx>{`
        :root {
          --cyber-green: #a8fd2d;
          --electric-cyan: #22d3ee;
          --cosmic-pink: #ff1493;
          --midnight-black: #000000;
          --glass-bg: rgba(10, 14, 43, 0.3);
          --card-blur: 12px;
          --background-color: var(--midnight-black);
          --text-color: white;
          --card-border-color: rgba(255, 255, 255, 0.1);
          --card-text-color: white;
          --card-bg: #000000;
          --input-bg: #1a202c;
          --input-text: white;
          --input-border: #4a5568;
          --modal-bg: rgba(10, 14, 43, 0.3);
          --modal-border: rgba(255, 255, 255, 0.1);
          --error-text: var(--cosmic-pink);
          --gray-text: #e5e7eb;
          --gray-text-secondary: #9ca3af;
        }

        .theme-white {
          --background-color: #f5f5f5;
          --text-color: #333;
          --card-border-color: #d1d5db;
          --card-text-color: #333;
          --card-bg: rgba(255, 255, 255, 0.8);
          --glass-bg: rgba(255, 255, 255, 0.3);
          --input-bg: #ffffff;
          --input-text: #333;
          --input-border: #d1d5db;
          --modal-bg: rgba(255, 255, 255, 0.3);
          --modal-border: #d1d5db;
          --error-text: #e11d48;
          --gray-text: #4b5563;
          --gray-text-secondary: #6b7280;
        }

        .theme-pink {
          --background-color: #fff0f6;
          --text-color: #333;
          --card-border-color: #f9a8d4;
          --card-text-color: #333;
          --card-bg: rgba(255, 240, 246, 0.8);
          --glass-bg: rgba(255, 240, 246, 0.3);
          --input-bg: #ffffff;
          --input-text: #333;
          --input-border: #f9a8d4;
          --modal-bg: rgba(255, 240, 246, 0.3);
          --modal-border: #f9a8d4;
          --error-text: #be123c;
          --gray-text: #4b5563;
          --gray-text-secondary: #6b7280;
        }

        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
          font-family: 'Inter', sans-serif;
        }

        body {
          color: var(--text-color);
          margin: 0;
          overflow-x: hidden;
        }

        .theme-black body {
          background-color: #000000;
        }

        .theme-white body {
          background-color: #c0c0c0;
        }

        .theme-pink body {
          background-color: #c71585;
        }

        .theme-black .agent-card:not(.connect-school-card),
        .theme-black .chatbot-container {
          background: #000000 !important;
          border: 1px solid rgba(255, 255, 255, 0.1);
        }

        .theme-black .agent-card:not(.connect-school-card)::before,
        .theme-black .chatbot-container::before {
          background: #000000 !important;
          backdrop-filter: none !important;
        }

        .fixed-header {
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 120px;
          background: #000000 !important;
          border-bottom: 1px solid rgba(255, 255, 255, 0.1);
          z-index: 10;
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 0 16px;
        }

        .chat-button, .iq-button, .health-button, .content-button {
          background: rgba(10, 14, 43, 0.3) !important;
          border: 1px solid rgba(255, 255, 255, 0.1) !important;
          border-radius: 8px;
          padding: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .chat-button:hover, .iq-button:hover, .health-button:hover, .content-button:hover {
          border-color: var(--cyber-green) !important;
          box-shadow: 0 0 10px var(--cyber-green) !important;
        }

        .chat-button:active, .iq-button:active, .health-button:active, .content-button:active {
          transform: scale(0.95);
        }

        .chat-button svg, .health-button svg, .content-button svg {
          stroke: var(--cyber-green) !important;
          width: 24px;
          height: 24px;
        }

        .iq-button {
          font-size: 14px;
          font-weight: 600;
          padding: 8px 16px;
          color: white !important;
        }

        .health-button, .content-button {
          font-size: 14px;
          font-weight: 600;
          padding: 8px 16px;
        }

        .container {
          max-width: 448px;
          margin: 120px auto 0;
          padding: 16px;
          display: flex;
          flex-direction: column;
          gap: 16px;
          min-height: calc(100vh - 120px);
          overflow-y: auto;
        }

        @media (min-width: 769px) {
          .container {
            max-width: 1000px;
            padding: 24px;
          }

          .agent-card,
          .chatbot-container {
            padding: 20px;
          }
        }

        .agent-card,
        .chatbot-container {
          position: relative;
          background: var(--card-bg);
          border-radius: 15px;
          border: 1px solid var(--card-border-color);
          padding: 16px;
          transition: all 0.3s ease;
          overflow: hidden;
          word-wrap: break-word;
          width: 100%;
          will-change: transform;
          color: var(--card-text-color);
        }

        .agent-card::before,
        .chatbot-container::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: var(--glass-bg);
          backdrop-filter: blur(var(--card-blur));
          z-index: -1;
          border-radius: 15px;
        }

        .agent-card:hover,
        .chatbot-container:hover {
          box-shadow: 0 0 15px var(--cyber-green);
          border-color: var(--cyber-green);
        }

        .agent-card:active,
        .chatbot-container:active {
          box-shadow: 0 0 25px var(--cyber-green);
          border-color: var(--cyber-green);
        }

        .connect-school-card {
          background: linear-gradient(135deg, rgba(6, 182, 212, 0.1), rgba(8, 145, 178, 0.1)) !important;
          border: 1px solid rgba(6, 182, 212, 0.3) !important;
          color: white !important;
        }

        .connect-school-card::before {
          background: none !important;
          backdrop-filter: none !important;
        }

        .connect-school-card:hover {
          box-shadow: 0 0 20px rgb(0, 217, 255) !important;
          border-color: #06b6d4 !important;
        }

        .connect-school-card:active {
          box-shadow: 0 0 30px rgba(6, 182, 212, 0.7) !important;
        }

        .connect-school-card .text-gray-200 {
          color: rgb(0, 0, 0) !important;
        }

        .connect-school-card .text-gray-400 {
          color: rgb(0, 0, 0) !important;
        }

        .connect-school-card .text-cosmic-pink {
          color: #ff1493 !important;
        }

        .connect-school-card input {
          background-color: #1a202c !important;
          color: white !important;
          border-color: #4a5568 !important;
        }

        .connect-school-card input:focus {
          border-color: #06b6d4 !important;
        }

        .connect-button {
          background: linear-gradient(45deg, #06b6d4, #22d3ee);
          border: none;
          color: white;
          font-weight: 600;
          transition: transform 0.3s ease, box-shadow 0.3s ease;
        }

        .connect-button:hover {
          transform: scale(1.05);
          box-shadow: 0 0 15px rgba(6, 182, 212, 0.7);
        }

        .connect-button:active {
          transform: scale(0.95);
        }

        .agent-card *,
        .chatbot-container * {
          position: relative;
          z-index: 1;
        }

        .agent-icon {
          width: 48px;
          height: 48px;
          background: linear-gradient(45deg, var(--electric-cyan), var(--cyber-green));
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 20px;
          color: white;
        }

        .chatbot-container {
          flex: 1;
          display: flex;
          flex-direction: column;
          overflow: hidden;
        }

        .progress-bar-container {
          position: relative;
          width: 100%;
          background: rgba(255, 255, 255, 0.2);
          border-radius: 9999px;
          height: 16px;
          overflow: hidden;
        }

        .progress-bar {
          height: 100%;
          background: linear-gradient(to right, var(--electric-cyan), var(--cyber-green));
          border-radius: 9999px;
          transition: width 0.5s ease-in-out;
        }

        .language-button {
          padding: 8px 16px;
          border-radius: 8px;
          border: 1px solid var(--card-border-color);
          background: var(--glass-bg);
          backdrop-filter: blur(var(--card-blur));
          color: var(--text-color);
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .language-button:hover {
          border-color: var(--cyber-green);
          color: var(--cyber-green);
        }

        .language-button.selected {
          background: linear-gradient(45deg, var(--electric-cyan), var(--cyber-green));
          border-color: var(--cyber-green);
          color: white;
          box-shadow: 0 0 15px var(--cyber-green);
          transform: scale(1.05);
        }

        .theme-circle {
          border-radius: 50%;
          border: 1px solid var(--card-border-color);
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .theme-circle:hover {
          border-color: var(--cyber-green);
          box-shadow: 0 0 10px var(--cyber-green);
        }

        .theme-circle.active {
          border-color: var(--cyber-green);
          box-shadow: 0 0 15px var(--cyber-green);
        }

        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.7);
          display: flex;
          justify-content: center;
          align-items: center;
          z-index: 1000;
        }

        .modal-content {
          background: var(--modal-bg);
          backdrop-filter: blur(var(--card-blur));
          border: 1px solid var(--modal-border);
          border-radius: 15px;
          padding: 20px;
          max-width: 90%;
          max-height: 80vh;
          overflow-y: auto;
          position: relative;
          box-shadow: 0 0 20px var(--cyber-green);
          color: var(--text-color);
        }

        .modal-close {
          position: absolute;
          top: 10px;
          right: 10px;
          background: transparent;
          border: none;
          color: var(--cyber-green);
          font-size: 24px;
          cursor: pointer;
          transition: color 0.3s ease;
        }

        .modal-close:hover {
          color: var(--cosmic-pink);
        }

        .text-gray-200 {
          color: var(--gray-text);
        }

        .text-gray-400 {
          color: var(--gray-text-secondary);
        }

        .text-cosmic-pink {
          color: var(--error-text);
        }

        input {
          background-color: var(--input-bg);
          color: var(--input-text);
          border-color: var(--input-border);
        }

        input:focus {
          border-color: var(--cyber-green);
          outline: none;
        }

        .banner-image {
          position: relative;
          width: 100%;
          height: auto;
          object-fit: cover;
          aspect-ratio: 1230 / 300;
          margin: 0;
          border-top-left-radius: 15px;
          border-top-right-radius: 15px;
          display: block;
        }

        @keyframes fade-in {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }

        .animate-fade-in {
          animation: fade-in 0.3s ease-out;
        }

        @media (max-width: 768px) {
          .container {
            padding: 12px;
            gap: 12px;
          }

          .agent-card,
          .chatbot-container {
            padding: 12px;
          }

          .agent-icon {
            width: 40px;
            height: 40px;
            font-size: 18px;
          }

          .fixed-header {
            height: 100px;
          }

          .container {
            margin-top: 100px;
            min-height: calc(100vh - 100px);
          }

          .progress-bar-container {
            height: 12px;
          }

          .language-button {
            padding: 6px 12px;
            font-size: 12px;
          }

          .chat-button, .iq-button, .health-button, .content-button {
            padding: 6px;
          }

          .iq-button, .health-button, .content-button {
            font-size: 12px;
            padding: 6px 12px;
          }

          .chat-button svg, .health-button svg, .content-button svg {
            width: 20px;
            height: 20px;
          }

          .modal-content {
            max-width: 95%;
            padding: 15px;
          }

          .modal-close {
            font-size: 20px;
          }
        }
      `}</style>

      <div className="fixed-header">
        <div className="button-group-left" style={{ display: 'flex', gap: '8px' }}>
          <button
            className="chat-button"
            title="Chat"
            onClick={handleChatButtonClick}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
            </svg>
          </button>
          <button
            className="content-button"
            title="Library"
            onClick={handleContentButtonClick}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M4 19.5v-15A2.5 2.5 0 0 1 6.5 2H20v20H6.5a2.5 2.5 0 0 1 0-5H20" />
            </svg>
          </button>
        </div>
        <button onClick={() => setCurrentView("profile")}>
          <Lottie
            animationData={octopusAnimation}
            loop={true}
            style={{ width: "120px", height: "80px", cursor: "pointer" }}
          />
        </button>
        <div className="button-group-right" style={{ display: 'flex', gap: '8px' }}>
          <button className="iq-button" onClick={handleIQButtonClick}>
            IQ
          </button>
          <button
            className="health-button"
            title="Health"
            onClick={handleHealthButtonClick}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M20.38 3.46 16 2a4 4 0 0 1-8 0L3.62 3.46a2 2 0 0 0-1.34 2.23l.58 3.47a1 1 0 0 0 .99.84H6v10c0 1.1.9 2 2 2h8a2 2 0 0 0 2-2V10h2.15a1 1 0 0 0 .99-.84l.58-3.47a2 2 0 0 0-1.34-2.23z" />
            </svg>
          </button>
        </div>
      </div>
      {currentView === "chat" ? (
        <div className="container">
          <div className="chatbot-container">
            <Chatbot
              userId={userData.userId}
              ageYears={userData.ageYears}
              ageDays={userData.totalAgeDays}
              language={language}
              onChatComplete={handleChatCompletion}
            />
          </div>
        </div>
      ) : currentView === "leaderboard" ? (
        <RankListComponent
          userId={userData.userId}
          userName={userData.childName}
          onBack={() => setCurrentView("profile")}
        />
      ) : currentView === "iq" ? (
        <DroctoIQDashboard userId={userData.userId} />
      ) : currentView === "socialmedia" ? (
        <SocialMedia
          userId={userData.userId}
          userName={userData.childName}
          onBack={() => setCurrentView("profile")}
        />
      ) : currentView === "health" ? (
        <Health
          userId={userData.userId}
          userName={userData.childName}
          onBack={() => setCurrentView("profile")}
          ageYears={userData.ageYears}
          ageDays={userData.totalAgeDays}
          theme={theme}
        />
      ) : currentView === "content" ? (
        <Library
          userId={userData.userId}
          userName={userData.childName}
          onBack={() => setCurrentView("profile")}
        />
      ) : (
        <div className="container">
          <ProfileCard />
          <ConnectWithSchool
            schoolDetails={userData.schoolDetails}
            onConnect={connectSchool}
          />
          <SettingsCard />
          <PlanAndSupportCard plan={userData.plan} />
        </div>
      )}
      <Modal isOpen={isParentExpanded} onClose={() => setIsParentExpanded(false)}>
        <ParentDashboard
          childName={userData.childName}
          ageYears={userData.ageYears}
          ageDays={userData.displayAgeDays}
          score={points}
          stateRank={stateRank}
          completionPercentage={dailyPercentage}
          completedTasks={completedTasks}
          tasks={tasks}
          userId={userData.userId}
          isExpanded={isParentExpanded}
          setIsExpanded={setIsParentExpanded}
        />
      </Modal>
    </div>
  );
};

export default ThinkyPlayProfile;